import { RulesetApplicability } from "types/DefaultRules/DefaultRules";

/**
 * CLE-2842 step 2 — the scope of an additive rule (MPPR, Case Rate, Package, Exploding Fee
 * Schedule, Inpatient, Unit Cap), now shaped like the ladder types':
 *   TYPICAL  — a template: no provider, no payer
 *   SCOPED   — "Payer-Provider rule": a provider, and a payer or All payers (null)
 *   FALLBACK — "Fallback rule": no provider (every provider), and a payer or All payers (null)
 *
 * Payer values in form state:
 *   `undefined` — no payer chosen yet (a Payer-Provider rule cannot be saved like this)
 *   `null`      — All payers
 *   a number    — that payer
 */

export const ALL_PAYERS_LABEL = "All payers";

/** The payer pickers' "All payers" choice: a rule with `payerId: null`. */
export const ALL_PAYERS_OPTION: { name: string; value: null } = {
  name: ALL_PAYERS_LABEL,
  value: null,
};

export interface IRuleScope {
  applicability: RulesetApplicability;
  providerId: string | null;
  payerId: number | null | undefined;
}

interface IScopedRow {
  applicability?: RulesetApplicability | null;
  isDefault?: boolean | null;
  providerId?: string | null;
  payerId?: number | string | null;
}

interface INamedScopedRow extends IScopedRow {
  provider?: { id?: string; name?: string | null } | null;
  payer?: { id?: number; name?: string | null } | null;
}

interface IPickerOption<TValue> {
  name?: string;
  value?: TValue;
}

/**
 * The scope a rule takes when its applicability changes: a provider only survives on a
 * Payer-Provider rule, and a Typical template has neither. The payer is carried between
 * Payer-Provider and Fallback, so converting "this provider, Aetna" gives "Aetna, every provider".
 */
export function scopeForApplicability<TScope extends IRuleScope>(
  scope: TScope,
  next: RulesetApplicability
): TScope {
  return {
    ...scope,
    applicability: next,
    providerId: next === "SCOPED" ? scope.providerId : null,
    payerId: next === "TYPICAL" ? undefined : scope.payerId,
  };
}

/** Whether the provider/payer part of a rule is complete enough to save. */
export function isScopeComplete(scope: IRuleScope): boolean {
  if (scope.applicability !== "SCOPED") return true;
  return !!scope.providerId && scope.payerId !== undefined;
}

/** The provider/payer ids a create or update body carries for this scope (null = none / all). */
export function scopeRequestFields(scope: IRuleScope): {
  providerId: string | null;
  payerId: number | null;
} {
  return {
    providerId: scope.applicability === "SCOPED" ? scope.providerId : null,
    payerId: scope.applicability === "TYPICAL" ? null : scope.payerId ?? null,
  };
}

/**
 * A loaded row's applicability — the column, else what the legacy `isDefault` flag meant for these
 * six types (a template). Undefined while the row has not loaded.
 */
export function savedApplicabilityOf(
  row: IScopedRow | null | undefined
): RulesetApplicability | undefined {
  if (!row) return undefined;
  return row.applicability ?? (row.isDefault ? "TYPICAL" : "SCOPED");
}

/**
 * The `applicability` an update body carries: only when the save converts the rule between
 * Payer-Provider and Fallback. An unconverted edit sends no applicability, exactly as before.
 */
export function scopeUpdateFields(
  current: RulesetApplicability,
  saved: RulesetApplicability | undefined
): { applicability?: RulesetApplicability } {
  return saved !== undefined && current !== saved
    ? { applicability: current }
    : {};
}

/**
 * Whether an edit page offers, after saving, to propagate the change to the rule's copies. Only a
 * rule that was already a Typical template or a Fallback rule before this save can have copies,
 * so the decision keys on the applicability the rule was loaded with. A save that converts the
 * rule (Payer-Provider ↔ Fallback) never offers it and returns to the list: the server clears a
 * converted rule's copy links. With no loaded applicability it falls back to the current one.
 */
export function shouldOfferPropagation(
  current: RulesetApplicability,
  saved: RulesetApplicability | undefined
): boolean {
  const before = saved ?? current;
  return before !== "SCOPED" && current === before;
}

/**
 * The scope a Copy page starts from: a Payer-Provider copy of the source, or a Typical template
 * when the caller asks for one (`?asDefault=true`). A template's or global fallback's missing payer
 * is "not chosen yet", not All payers.
 */
export function copyScopeOf(
  source: IScopedRow,
  asDefault: boolean
): IRuleScope {
  if (asDefault) {
    return { applicability: "TYPICAL", providerId: null, payerId: undefined };
  }
  const sourcePayerId =
    source.payerId == null ? source.payerId : Number(source.payerId);
  return {
    applicability: "SCOPED",
    providerId: source.providerId ?? null,
    payerId:
      sourcePayerId ??
      (savedApplicabilityOf(source) === "SCOPED" ? null : undefined),
  };
}

/** The confirmation shown before an edit page converts a rule between Payer-Provider and Fallback. */
export function conversionMessage(
  next: RulesetApplicability,
  payerLabel: string
): { header: string; message: string } {
  if (next === "FALLBACK") {
    return {
      header: "Convert to fallback rule?",
      message: `This rule will apply to every provider that has no more specific rule of this type, for ${payerLabel}. Continue?`,
    };
  }
  return {
    header: "Convert to payer-provider rule?",
    message: `This rule will apply only to the provider you pick below, for ${payerLabel}, instead of to every provider. Continue?`,
  };
}

const rowPayerId = (row: INamedScopedRow): number | null => {
  if (row.payer?.id != null) return row.payer.id;
  return row.payerId == null ? null : Number(row.payerId);
};

/**
 * The scope fields of an MPPR / Case Rate / Package edit form (react-hook-form), from the loaded
 * row: a Payer-Provider rule on All payers shows the "All payers" choice, and a Fallback rule's
 * payer lives in `fallbackPayerId`.
 */
export function editFormScope(row: INamedScopedRow | null | undefined): {
  applicability: RulesetApplicability;
  isDefault: boolean;
  provider: IPickerOption<string>;
  payerName: IPickerOption<number | null>;
  fallbackPayerId: number | null;
} {
  const applicability = savedApplicabilityOf(row) ?? "SCOPED";
  const provider = {
    name: row?.provider?.name ?? undefined,
    value: row?.provider?.id,
  };
  if (!row) {
    return {
      applicability,
      isDefault: false,
      provider,
      payerName: { name: undefined, value: undefined },
      fallbackPayerId: null,
    };
  }
  const payerId = rowPayerId(row);
  return {
    applicability,
    isDefault: applicability !== "SCOPED",
    provider,
    payerName:
      applicability === "SCOPED" && payerId === null
        ? ALL_PAYERS_OPTION
        : { name: row.payer?.name ?? undefined, value: row.payer?.id },
    fallbackPayerId: applicability === "FALLBACK" ? payerId : null,
  };
}

/**
 * The scope fields of an MPPR / Case Rate / Package copy form (react-hook-form): see
 * `copyScopeOf`. The provider and payer pickers are multi-select there, so each is a list — empty
 * when the source has nothing to pre-select (a Typical template, or a copy asked for as one).
 */
export function copyFormScope(
  row: INamedScopedRow | null | undefined,
  asDefault: boolean
): {
  applicability: RulesetApplicability;
  isDefault: boolean;
  provider: { name: string; value: string }[];
  payerName: { name: string; value: number | null }[];
  fallbackPayerId: number | null;
} {
  if (!row) {
    return {
      applicability: asDefault ? "TYPICAL" : "SCOPED",
      isDefault: asDefault,
      provider: [],
      payerName: [],
      fallbackPayerId: null,
    };
  }
  const scope = copyScopeOf(
    { ...row, payerId: row.payer?.id ?? row.payerId },
    asDefault
  );
  let payerName: { name: string; value: number | null }[] = [];
  if (scope.payerId === null) payerName = [ALL_PAYERS_OPTION];
  else if (scope.payerId !== undefined) {
    payerName = [{ name: row.payer?.name ?? "", value: scope.payerId }];
  }
  return {
    applicability: scope.applicability,
    isDefault: scope.applicability !== "SCOPED",
    provider: scope.providerId
      ? [{ name: row.provider?.name ?? "", value: scope.providerId }]
      : [],
    payerName,
    fallbackPayerId: scope.payerId ?? null,
  };
}
